import { forwardRef } from 'react'

const LoadingSpinner = forwardRef(({
  variant = 'ring',
  size = 'md',
  className = '',
  color = 'primary',
  'aria-label': ariaLabel = 'Loading',
  ...props
}, ref) => {
  // Size configurations
  const sizeClasses = {
    xs: 'w-4 h-4',
    sm: 'w-6 h-6', 
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  }

  // Color configurations
  const colorClasses = {
    primary: 'border-accent-foreground',
    secondary: 'border-text-secondary',
    success: 'border-status-success',
    warning: 'border-status-warning',
    danger: 'border-status-danger',
    muted: 'border-text-muted'
  }

  // Ring spinner component
  const RingSpinner = () => (
    <div
      ref={ref}
      className={`
        apple-spinner-ring
        ${sizeClasses[size]}
        ${className}
      `}
      role="status"
      aria-label={ariaLabel}
      style={{
        border: '2px solid transparent',
        borderTopColor: `var(--color-${colorClasses[color].replace('border-', '')})`,
        borderRadius: '50%',
        animation: 'apple-spin 1s linear infinite'
      }}
      {...props}
    />
  )

  // Dots spinner component  
  const DotsSpinner = () => {
    const dotSize = size === 'xs' ? 'w-1 h-1' : size === 'sm' ? 'w-1.5 h-1.5' : 'w-2 h-2'
    
    return (
      <div
        ref={ref}
        className={`apple-spinner-dots flex items-center space-x-1 ${className}`}
        role="status"
        aria-label={ariaLabel}
        {...props}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={`${dotSize} rounded-full`}
            style={{
              backgroundColor: `var(--color-${colorClasses[color].replace('border-', '')})`,
              animation: `apple-pulse 1.4s ease-in-out ${i * 0.16}s infinite both`
            }}
          />
        ))}
      </div>
    )
  }

  // Pulse spinner component
  const PulseSpinner = () => (
    <div
      ref={ref}
      className={`
        apple-spinner-pulse
        ${sizeClasses[size]}
        rounded-full
        ${className}
      `}
      role="status"
      aria-label={ariaLabel}
      style={{
        backgroundColor: `var(--color-${colorClasses[color].replace('border-', '')})`,
        animation: 'apple-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
      }}
      {...props}
    />
  )

  // Skeleton loader component
  const SkeletonSpinner = () => (
    <div
      ref={ref}
      className={`
        apple-spinner-skeleton
        ${sizeClasses[size]}
        rounded-lg
        ${className}
      `}
      role="status"
      aria-label={ariaLabel}
      style={{
        backgroundColor: `var(--color-surface-secondary)`,
        animation: 'apple-shimmer 2s ease-in-out infinite'
      }}
      {...props}
    />
  )

  // Render appropriate spinner variant
  switch (variant) {
    case 'dots':
      return <DotsSpinner />
    case 'pulse':
      return <PulseSpinner />
    case 'skeleton':
      return <SkeletonSpinner />
    case 'ring':
    default:
      return <RingSpinner />
  }
})

LoadingSpinner.displayName = 'LoadingSpinner'

export default LoadingSpinner
